"use client";

import type { FC } from "react";
import { ChevronRight, Globe2, LockKeyhole, X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Avatar, AvatarFallback, Badge, Button, Loading } from "@/app/components/ui";
import { SHARE_ACCESS, SHARE_ACCESS_OPTIONS } from "@/app/lib/constants";
import type { ShareAccess, ShareInvite } from "@/types";

interface ShareAccessSectionProps {
   access: ShareAccess;
   owner: { name: string; email: string };
   invites: ShareInvite[];
   disabled: boolean;
   removingEmail: string | null;
   onOpenSettings: () => void;
   onRemove: (email: string) => void;
}

const PersonAvatar: FC<{ label: string }> = ({ label }) => (
   <Avatar className="h-8 w-8">
      <AvatarFallback className="text-sm font-semibold">
         {label.charAt(0).toUpperCase()}
      </AvatarFallback>
   </Avatar>
);

export const ShareAccessSection: FC<ShareAccessSectionProps> = ({
   access,
   owner,
   invites,
   disabled,
   removingEmail,
   onOpenSettings,
   onRemove
}) => {
   const translate = useTranslations("Home");
   const locale = useLocale();
   const accessOption = SHARE_ACCESS_OPTIONS[access];
   const AccessIcon = access === SHARE_ACCESS.LINK ? Globe2 : LockKeyhole;
   const ownerLabel = owner.email || owner.name;
   const now = Date.now();

   return (
      <section className="space-y-2">
         <h3 className="text-sm font-medium text-foreground">
            {translate("whoHasAccess")}
         </h3>
         <Button
            type="button"
            variant="ghost"
            onClick={onOpenSettings}
            className="h-auto w-full justify-start gap-3 whitespace-normal rounded-xl border border-border px-3 py-2 text-left hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
         >
            <AccessIcon size={18} className="shrink-0" />
            <span className="min-w-0 flex-1">
               <span className="block text-sm font-medium">
                  {translate(accessOption.labelKey)}
               </span>
               <span className="block whitespace-normal text-xs font-normal text-muted-foreground">
                  {translate(accessOption.descriptionKey)}
               </span>
            </span>
            <ChevronRight size={18} className="shrink-0 text-muted-foreground" />
         </Button>

         <ul className="space-y-0.5">
            <li className="flex items-center gap-2.5 px-1 py-1.5">
               <PersonAvatar label={ownerLabel} />
               <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                  {translate("shareOwnerYou", { name: ownerLabel })}
               </p>
               <span className="text-sm text-muted-foreground">
                  {translate("shareOwner")}
               </span>
            </li>
            {invites.map((invite) => {
               const expiresAt = new Date(invite.expiresAt);
               const isExpired = expiresAt.getTime() <= now;
               const inviteLabel = invite.email || invite.name || "";

               return (
                  <li
                     key={invite.email}
                     className="flex items-center gap-2.5 px-1 py-1.5"
                  >
                     <PersonAvatar label={inviteLabel} />
                     <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                        {inviteLabel}
                     </p>
                     {isExpired ? (
                        <Badge
                           variant="outline"
                           className="shrink-0 text-muted-foreground"
                        >
                           {translate("shareInviteExpired")}
                        </Badge>
                     ) : (
                        <span className="shrink-0 text-xs text-muted-foreground">
                           {translate("shareInviteExpires", {
                              date: expiresAt.toLocaleDateString(locale, {
                                 dateStyle: "medium"
                              })
                           })}
                        </span>
                     )}
                     <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onRemove(invite.email)}
                        disabled={disabled}
                        aria-label={translate("removeShareInvite", {
                           email: invite.email
                        })}
                        className="h-8 w-8 shrink-0 rounded-xl hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
                     >
                        {removingEmail === invite.email ? (
                           <Loading size="sm" />
                        ) : (
                           <X size={16} />
                        )}
                     </Button>
                  </li>
               );
            })}
         </ul>
      </section>
   );
};
